/**
 * VerifyWizard — pre-auth driver verification / registration flow.
 * Reconstructed from legacy verify-app.js (Phase 2C).
 *
 * Two paths:
 *   - Existing driver (found, no password): verify/update profile + set password
 *   - New driver (not found): fill profile, submit for manager review (no password)
 *
 * API contracts:
 *   POST /api/auth/profile-lookup   → { found, has_password, employee }
 *   POST /api/auth/complete-profile → { mode: 'updated' } or { request_id }
 */

import React, { useState } from 'react';
import { C, FONT, GLASS, GLASS_STRONG, INPUT, BTN, BTN_DISABLED } from '../driver-tokens.js';
import { apiPost } from './driver-session.js';

interface VerifyWizardProps {
  onComplete: () => void;
  onBack: () => void;
}

interface ProfileLookupResponse {
  found?: boolean;
  has_password?: boolean;
  employee?: Record<string, string | undefined>;
  error?: string;
}

interface CompleteProfileResponse {
  mode?: string;
  request_id?: string;
  error?: string;
}

const PROFILE_FIELDS = [
  'first_name', 'last_name', 'email', 'phone',
  'emergency_contact_name', 'emergency_contact_phone', 'emergency_contact_relationship',
  'address_line1', 'address_line2', 'address_city', 'address_state', 'address_zip',
  'dob', 'dl_number', 'shirt_size', 'pants_size', 'jacket_size',
] as const;

type FormFields = Record<typeof PROFILE_FIELDS[number] | 'password' | 'confirm', string>;

const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

const STEPS = [
  { title: 'Welcome', required: [] as string[] },
  { title: 'Driver ID', required: [] as string[] },
  { title: 'Personal Info', required: ['first_name', 'last_name'] },
  { title: 'Emergency & Address', required: [] as string[] },
  { title: 'Uniforms', required: [] as string[] },
  { title: 'Set Password & Submit', required: [] as string[] },
];

function checkPassword(pw: string) {
  return {
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /[0-9]/.test(pw),
    special: /[!@#$%^&*]/.test(pw),
  };
}

function passwordValid(pw: string) {
  var r = checkPassword(pw);
  return r.length && r.upper && r.lower && r.digit && r.special;
}

export default function VerifyWizard({ onComplete, onBack }: VerifyWizardProps) {
  var [step, setStep] = useState(0);
  var [driverId, setDriverId] = useState('');
  var [lookupState, setLookupState] = useState<'idle' | 'loading' | 'found' | 'not_found' | 'has_password' | 'error'>('idle');
  var [isNew, setIsNew] = useState(false);
  var [form, setForm] = useState<FormFields>({
    first_name: '', last_name: '', email: '', phone: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
    address_line1: '', address_line2: '', address_city: '', address_state: '', address_zip: '',
    dob: '', dl_number: '', shirt_size: '', pants_size: '', jacket_size: '',
    password: '', confirm: '',
  });
  var [error, setError] = useState('');
  var [submitting, setSubmitting] = useState(false);
  var [done, setDone] = useState(false);
  var [result, setResult] = useState<CompleteProfileResponse | null>(null);

  function updateField(f: string, v: string) { setForm(function(p) { return { ...p, [f]: v }; }); }

  function canProceed(): boolean {
    if (step === 1) return lookupState === 'found' || lookupState === 'not_found';
    return STEPS[step].required.every(function(f) { return !!form[f as keyof FormFields]; });
  }

  function handleLookup() {
    if (driverId.length !== 7) return;
    setLookupState('loading');
    setError('');
    apiPost<ProfileLookupResponse>('/api/auth/profile-lookup', { driver_id: driverId }).then(function(data) {
      if (data.error) {
        setLookupState('error');
        setError(data.error);
        return;
      }
      if (data.found) {
        if (data.has_password) {
          setLookupState('has_password');
          return;
        }
        setLookupState('found');
        setIsNew(false);
        // Pre-fill form from employee record
        if (data.employee) {
          var prefill: Partial<FormFields> = {};
          for (var i = 0; i < PROFILE_FIELDS.length; i++) {
            var f = PROFILE_FIELDS[i];
            if (data.employee[f]) { prefill[f] = data.employee[f]; }
          }
          setForm(function(p) { return { ...p, ...prefill }; });
        }
      } else {
        setLookupState('not_found');
        setIsNew(true);
      }
    }).catch(function() {
      setLookupState('error');
      setError('Connection error. Please try again.');
    });
  }

  function handleSubmit() {
    setSubmitting(true);
    setError('');
    var payload: Record<string, string | undefined> = {
      driver_id: driverId,
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email || undefined,
      phone: form.phone || undefined,
      emergency_contact_name: form.emergency_contact_name || undefined,
      emergency_contact_phone: form.emergency_contact_phone || undefined,
      emergency_contact_relationship: form.emergency_contact_relationship || undefined,
      address_line1: form.address_line1 || undefined,
      address_line2: form.address_line2 || undefined,
      address_city: form.address_city || undefined,
      address_state: form.address_state || undefined,
      address_zip: form.address_zip || undefined,
      dob: form.dob || undefined,
      dl_number: form.dl_number || undefined,
      shirt_size: form.shirt_size || undefined,
      pants_size: form.pants_size || undefined,
      jacket_size: form.jacket_size || undefined,
    };
    if (!isNew) {
      payload.password = form.password;
      payload.confirm = form.confirm;
    }
    apiPost<CompleteProfileResponse>('/api/auth/complete-profile', payload).then(function(data) {
      setSubmitting(false);
      if (data.error) { setError(data.error); return; }
      setResult(data);
      setDone(true);
    }).catch(function() {
      setSubmitting(false);
      setError('Connection error. Please try again.');
    });
  }

  // --- helper renderers ---

  function renderInput(label: string, field: string, opts?: { required?: boolean; type?: string; inputMode?: string; maxLength?: number; placeholder?: string }) {
    opts = opts || {};
    var isRequired = opts.required && !form[field as keyof FormFields];
    return React.createElement('div', { style: { marginBottom: 14 } },
      React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 } }, label, opts.required ? ' *' : ''),
      React.createElement('input', {
        type: opts.type || 'text',
        inputMode: opts.inputMode || undefined,
        maxLength: opts.maxLength || undefined,
        style: { ...INPUT, borderColor: isRequired ? C.red : 'rgba(255,255,255,0.08)' },
        value: form[field as keyof FormFields] || '',
        onChange: function(e: React.ChangeEvent<HTMLInputElement>) { updateField(field, e.target.value); },
        placeholder: opts.placeholder || '',
      })
    );
  }

  function renderSelect(label: string, field: string, options: string[]) {
    return React.createElement('div', { style: { marginBottom: 14 } },
      React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 } }, label),
      React.createElement('select', {
        style: { ...INPUT, appearance: 'none' as const },
        value: form[field as keyof FormFields] || '',
        onChange: function(e: React.ChangeEvent<HTMLSelectElement>) { updateField(field, e.target.value); },
      },
        React.createElement('option', { value: '' }, 'Select...'),
        options.map(function(o) { return React.createElement('option', { key: o, value: o }, o); })
      )
    );
  }

  function renderRule(label: string, ok: boolean) {
    return React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11, color: ok ? C.green : C.textMuted, marginBottom: 2 } }, (ok ? '\u2713 ' : '\u2022 ') + label);
  }

  // --- done screen ---

  if (done) {
    var isUpdated = result && result.mode === 'updated';
    return React.createElement('div', { style: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: C.dark } },
      React.createElement('div', { style: { ...GLASS_STRONG, padding: 40, maxWidth: 420, width: '100%', borderRadius: 20, textAlign: 'center' } },
        React.createElement('div', { style: { fontSize: 56, marginBottom: 16 } }, '\u2713'),
        React.createElement('div', { style: { fontFamily: FONT.headline, fontSize: 24, color: C.text, marginBottom: 8 } },
          isUpdated ? 'Profile Complete!' : 'Registration Submitted'
        ),
        React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 14, color: C.silver, marginBottom: 20, lineHeight: 1.6 } },
          isUpdated
            ? "Your profile is verified and your password is set. You're ready to log in."
            : "Your registration has been submitted for manager review. You'll be able to log in once approved."
        ),
        !isUpdated && result && result.request_id && React.createElement('div', {
          style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, ...GLASS, padding: 12, borderRadius: 10, marginBottom: 20 }
        }, 'Reference: ' + result.request_id.slice(0, 8)),
        React.createElement('button', { style: BTN, onClick: onComplete },
          isUpdated ? 'Go to Login \u2192' : 'Done'
        )
      )
    );
  }

  // --- step content ---

  var content: React.ReactNode;
  switch (step) {
    case 0:
      content = React.createElement('div', { style: { textAlign: 'center', padding: 20 } },
        React.createElement('img', { src: '../assets/images/pwd_logo.png', style: { width: 100, marginBottom: 20, opacity: 0.9 }, alt: 'PWD' }),
        React.createElement('div', { style: { fontFamily: FONT.headline, fontSize: 24, color: C.text, marginBottom: 8 } }, 'Verify Your Profile'),
        React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 13, color: C.silver, lineHeight: 1.6, marginBottom: 20 } },
          "Your manager sent you this link to set up your driver account. You'll verify your information and create a password."
        ),
        React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, ...GLASS, padding: 12, borderRadius: 10 } },
          "You'll need: your 7-digit FedEx Driver ID, emergency contact info, and home address."
        )
      );
      break;

    case 1:
      content = React.createElement('div', null,
        React.createElement('div', { style: { fontFamily: FONT.headline, fontSize: 20, color: C.text, marginBottom: 16 } }, 'Enter Your Driver ID'),
        React.createElement('div', { style: { marginBottom: 14 } },
          React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 } }, 'DRIVER ID (7 DIGITS) *'),
          React.createElement('input', {
            type: 'text', inputMode: 'numeric', maxLength: 7,
            style: { ...INPUT, fontSize: 24, textAlign: 'center' as const, letterSpacing: '0.15em', fontFamily: "'Courier New', monospace" },
            value: driverId,
            onChange: function(e: React.ChangeEvent<HTMLInputElement>) {
              var v = e.target.value.replace(/\D/g, '').slice(0, 7);
              setDriverId(v);
              setLookupState('idle');
              setError('');
            },
            placeholder: '1234567',
            autoFocus: true,
          })
        ),
        React.createElement('button', {
          style: driverId.length === 7 && lookupState !== 'loading' ? BTN : BTN_DISABLED,
          disabled: driverId.length !== 7 || lookupState === 'loading',
          onClick: handleLookup,
        }, lookupState === 'loading' ? 'Looking up...' : 'Look Up My Profile'),

        lookupState === 'found' && React.createElement('div', {
          style: { fontFamily: FONT.mono, fontSize: 13, color: C.green, textAlign: 'center', marginTop: 16, ...GLASS, padding: 14, borderRadius: 10 }
        }, '\u2713 Found! Welcome, ' + form.first_name + ' ' + form.last_name + '. Your info has been loaded.'),

        lookupState === 'not_found' && React.createElement('div', {
          style: { fontFamily: FONT.mono, fontSize: 13, color: C.orange, textAlign: 'center', marginTop: 16, ...GLASS, padding: 14, borderRadius: 10 }
        }, "No existing record found. That's OK \u2014 you can fill out your info as a new driver. A manager will review and approve your account."),

        lookupState === 'has_password' && React.createElement('div', {
          style: { fontFamily: FONT.mono, fontSize: 13, color: C.orange, textAlign: 'center', marginTop: 16, ...GLASS, padding: 14, borderRadius: 10 }
        }, 'This account is already set up. ',
          React.createElement('button', {
            style: { background: 'none', border: 'none', color: C.gold, fontFamily: FONT.mono, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' },
            onClick: onBack,
          }, 'Go to login \u2192')
        )
      );
      break;

    case 2:
      content = React.createElement('div', null,
        !isNew && React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11, color: C.green, ...GLASS, padding: 10, borderRadius: 8, marginBottom: 14 } },
          '\u2713 Existing record found \u2014 verify or update your info below'
        ),
        isNew && React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11, color: C.orange, ...GLASS, padding: 10, borderRadius: 8, marginBottom: 14 } },
          'New driver \u2014 fill out your information below'
        ),
        renderInput('First Name', 'first_name', { required: true }),
        renderInput('Last Name', 'last_name', { required: true }),
        renderInput('Email', 'email', { type: 'email' }),
        renderInput('Phone', 'phone', { type: 'tel' })
      );
      break;

    case 3:
      content = React.createElement('div', null,
        React.createElement('div', { style: { fontFamily: FONT.headline, fontSize: 18, color: C.text, marginBottom: 12 } }, 'Emergency Contact'),
        renderInput('Contact Name', 'emergency_contact_name'),
        renderInput('Contact Phone', 'emergency_contact_phone', { type: 'tel' }),
        renderInput('Relationship', 'emergency_contact_relationship'),
        React.createElement('div', { style: { borderTop: '1px solid rgba(255,255,255,0.06)', margin: '20px 0', paddingTop: 16 } }),
        React.createElement('div', { style: { fontFamily: FONT.headline, fontSize: 18, color: C.text, marginBottom: 12 } }, 'Address & ID'),
        React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted, marginBottom: 12, ...GLASS, padding: 10, borderRadius: 8 } },
          'Stored securely. Only visible to HR personnel.'
        ),
        renderInput('Address Line 1', 'address_line1'),
        renderInput('Address Line 2', 'address_line2'),
        renderInput('City', 'address_city'),
        renderInput('State', 'address_state'),
        renderInput('ZIP', 'address_zip'),
        renderInput('Date of Birth', 'dob', { type: 'date' }),
        renderInput('Driver License #', 'dl_number')
      );
      break;

    case 4:
      content = React.createElement('div', null,
        React.createElement('div', { style: { fontFamily: FONT.headline, fontSize: 18, color: C.text, marginBottom: 12 } }, 'Uniform Sizes'),
        renderSelect('Shirt Size', 'shirt_size', SIZES),
        renderInput('Pants Size', 'pants_size'),
        renderSelect('Jacket Size', 'jacket_size', SIZES)
      );
      break;

    case 5: {
      var pwRules = checkPassword(form.password);
      var pwMatch = form.password.length > 0 && form.password === form.confirm;
      var canSubmitExisting = !!form.first_name && !!form.last_name && passwordValid(form.password) && pwMatch;
      var canSubmitNew = !!form.first_name && !!form.last_name;

      var reviewRows = [
        { label: 'Name', value: form.first_name + ' ' + form.last_name, goTo: 2 },
        { label: 'Driver ID', value: driverId, goTo: undefined },
        { label: 'Email', value: form.email || '\u2014', goTo: 2 },
        { label: 'Phone', value: form.phone || '\u2014', goTo: 2 },
        { label: 'Emergency', value: form.emergency_contact_name ? form.emergency_contact_name + ' (' + form.emergency_contact_phone + ')' : '\u2014', goTo: 3 },
        { label: 'Address', value: form.address_line1 ? [form.address_line1, form.address_city, form.address_state].filter(Boolean).join(', ') : '\u2014', goTo: 3 },
        { label: 'Shirt', value: form.shirt_size || '\u2014', goTo: 4 },
        { label: 'Pants', value: form.pants_size || '\u2014', goTo: 4 },
        { label: 'Jacket', value: form.jacket_size || '\u2014', goTo: 4 },
      ];

      content = React.createElement('div', null,
        React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 13, color: C.silver, marginBottom: 12 } }, 'Review your information:'),
        reviewRows.map(function(row) {
          return React.createElement('div', {
            key: row.label,
            style: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: row.goTo !== undefined ? 'pointer' : 'default' },
            onClick: row.goTo !== undefined ? function() { setStep(row.goTo as number); } : undefined,
          },
            React.createElement('span', { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: 'uppercase' } }, row.label),
            React.createElement('span', { style: { fontFamily: FONT.mono, fontSize: 13, color: row.value === '\u2014' ? C.textMuted : C.text } }, row.value)
          );
        }),

        // Password section (existing drivers only)
        !isNew && React.createElement('div', { style: { borderTop: '1px solid rgba(255,255,255,0.06)', margin: '20px 0', paddingTop: 16 } },
          React.createElement('div', { style: { fontFamily: FONT.headline, fontSize: 18, color: C.text, marginBottom: 12 } }, 'Create Your Password'),
          React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, marginBottom: 14, ...GLASS, padding: 10, borderRadius: 8 } },
            'TIP: Use something you\'ll remember. Must be 8+ characters with uppercase, lowercase, number, and special character.'
          ),
          React.createElement('div', { style: { marginBottom: 14 } },
            React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 } }, 'PASSWORD *'),
            React.createElement('input', { type: 'password', style: INPUT, value: form.password, onChange: function(e: React.ChangeEvent<HTMLInputElement>) { updateField('password', e.target.value); } })
          ),
          React.createElement('div', { style: { marginBottom: 14 } },
            React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 } }, 'CONFIRM PASSWORD *'),
            React.createElement('input', { type: 'password', style: INPUT, value: form.confirm, onChange: function(e: React.ChangeEvent<HTMLInputElement>) { updateField('confirm', e.target.value); } })
          ),
          form.password.length > 0 && React.createElement('div', { style: { marginBottom: 14 } },
            renderRule('8+ characters', pwRules.length),
            renderRule('Uppercase letter', pwRules.upper),
            renderRule('Lowercase letter', pwRules.lower),
            renderRule('Number', pwRules.digit),
            renderRule('Special character (!@#$%^&*)', pwRules.special),
            form.confirm.length > 0 && renderRule('Passwords match', pwMatch)
          )
        ),

        // New driver note
        isNew && React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 11, color: C.orange, ...GLASS, padding: 10, borderRadius: 8, marginTop: 16 } },
          "Your registration will be submitted for manager approval. You'll set your password after approval."
        ),

        // Submit button (replaces the normal Next button for step 5)
        React.createElement('div', { style: { marginTop: 20 } },
          React.createElement('button', {
            style: (isNew ? canSubmitNew : canSubmitExisting) && !submitting ? BTN : BTN_DISABLED,
            disabled: !(isNew ? canSubmitNew : canSubmitExisting) || submitting,
            onClick: handleSubmit,
          }, submitting ? 'Submitting...' : isNew ? 'Submit for Review' : 'Complete Profile & Set Password')
        )
      );
      break;
    }
  }

  // --- layout ---

  var progress = ((step + 1) / STEPS.length) * 100;

  return React.createElement('div', { style: { minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: C.dark } },
    React.createElement('div', { style: { ...GLASS_STRONG, maxWidth: 460, width: '100%', padding: 28, borderRadius: 20 } },

      // Progress bar (steps 1+)
      step > 0 && React.createElement('div', { style: { marginBottom: 20 } },
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: 6 } },
          React.createElement('span', { style: { fontFamily: FONT.mono, fontSize: 11, color: C.gold } }, 'Step ' + (step + 1) + ' of ' + STEPS.length),
          React.createElement('span', { style: { fontFamily: FONT.mono, fontSize: 11, color: C.silver } }, STEPS[step].title)
        ),
        React.createElement('div', { style: { height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2 } },
          React.createElement('div', { style: { height: '100%', width: progress + '%', background: C.gold, borderRadius: 2, transition: 'width 0.3s' } })
        )
      ),

      // Content area
      React.createElement('div', { style: { minHeight: 220, marginBottom: 20 } }, content),

      // Error
      error && React.createElement('div', { style: { fontFamily: FONT.mono, fontSize: 13, color: C.red, textAlign: 'center', marginBottom: 12 } }, error),

      // Navigation (not shown on step 5 which has its own submit button)
      step < 5 && React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between' } },
        step > 0 ? React.createElement('button', {
          style: { ...GLASS, padding: '12px 20px', color: C.silver, fontFamily: FONT.mono, fontSize: 14, cursor: 'pointer', minHeight: 48, border: '1px solid rgba(255,255,255,0.08)' },
          onClick: function() { setStep(step - 1); setError(''); },
        }, '\u2190 Back') : React.createElement('div'),

        step === 0
          ? React.createElement('button', { style: BTN, onClick: function() { setStep(1); } }, 'Get Started \u2192')
          : React.createElement('button', {
              style: canProceed() ? BTN : BTN_DISABLED,
              disabled: !canProceed(),
              onClick: function() { setStep(step + 1); setError(''); },
            }, 'Next \u2192')
      )
    )
  );
}
